/** Direct-materials requests (transformers, enclosures, drives, lighting, breakers). */
import { getSupplier } from '../../data/suppliers';
import type { ProcurementRequest } from './requests-types';
import {
  DAY,
  ag,
  approvals,
  approvedBy,
  by,
  checks,
  knownSupplier,
  person,
  personName,
  received,
  routedTo,
  seedRequest,
  supplierShort,
} from './seed-utils';

const SARAH = person('sarah-chen');
const MARCUS = person('marcus-hale');
const PRIYA = person('priya-raman');
const SAM = person('sam-whitfield');

function req1039(): ProcurementRequest {
  const marcus = personName('marcus-hale');
  return seedRequest({
    id: 'REQ-1039',
    title: 'Second supplier for dry-type transformers',
    description: `Qualify a second manufacturer of dry-type distribution transformers, 75 to 500 kVA. ${supplierShort('sup-stellar')} quotes lead times over 50 weeks as the federal energy efficiency rule and a specialty electrical steel shortage tighten capacity. Output is a capability and lead-time comparison to size a plan for a second supplier.`,
    requesterId: 'marcus-hale',
    channel: 'teams',
    department: 'Supply chain, Dallas HQ',
    costCenter: 'CC-2210',
    categoryId: 'cat-power',
    categoryLabel: 'Transformers & power distribution',
    type: 'direct',
    amountUsd: 2_200_000,
    amountNote: 'Estimated annual spend moved to a second supplier',
    recurring: true,
    neededInDays: 45,
    createdH: 20 * DAY,
    stage: 'sourcing',
    stageNote: 'RFI-2026-007 drafted, ready to release',
    buyPath:
      'Request for information to alternative manufacturers, then request for quote',
    suppliers: [
      knownSupplier(
        'sup-stellar',
        'Incumbent dry-type transformer supplier. Sets the baseline for lead time and price',
        'SA-STLR-2024-005',
      ),
    ],
    checks: checks([
      [
        'pass',
        'No similar request. The federal energy efficiency rule and lead-time alert (RSK-296) is the driver for this request.',
        [{ kind: 'risk-alert', alertId: 'RSK-296' }],
      ],
      [
        'flag',
        'A request for information collects information only. Three bids apply once a request for quote is issued.',
      ],
      [
        'pass',
        'CC-2210 has $1.1M remaining. The request for information carries no spend, and annual volume moves from the incumbent, so there is no net increase.',
      ],
      ['na', 'Not required. No system access or personal data in scope.'],
      [
        'na',
        'Not required for a request for information. Responses are covered by the standard confidentiality agreement.',
      ],
      [
        'flag',
        `${supplierShort('sup-stellar')} risk 58, Medium. The federal energy efficiency rule and a specialty electrical steel shortage put its capacity at risk.`,
        [{ kind: 'risk-alert', alertId: 'RSK-296' }],
      ],
    ]),
    approvals: approvals([
      { role: 'Budget owner', approver: MARCUS, doneH: 18 * DAY },
      { role: 'Category management', approver: SARAH, doneH: 17 * DAY },
    ]),
    linked: [
      {
        kind: 'sourcing-event',
        eventId: 'RFI-2026-007',
        title: 'RFI-2026-007',
        detail: 'Dry-type transformers, AI-drafted, in draft',
      },
      {
        kind: 'risk-alert',
        alertId: 'RSK-296',
        title: 'RSK-296',
        detail: `Energy efficiency rule and steel shortage at ${supplierShort('sup-stellar')}`,
      },
      {
        kind: 'contract',
        contractId: 'SA-STLR-2024-005',
        detail: 'Incumbent dry-type transformer supply',
      },
    ],
    recommendation: {
      title:
        'Release the AI-drafted request for information to dry-type transformer manufacturers',
      body: `The agent drafted RFI-2026-007 from the supply brief: lead time by kVA rating, energy efficiency rule compliance, UL listing and capacity for 75 to 500 kVA units. All approvals are in, so it is ready for ${marcus} to review and release.`,
    },
    activitySeeds: [
      received(20 * DAY, 'marcus-hale', 'through Teams'),
      ag(
        20 * DAY - 1,
        'Classified as Transformers & power distribution, Direct',
        `Linked to RSK-296 and the ${supplierShort('sup-stellar')} agreement.`,
      ),
      ag(
        19 * DAY,
        'Checks complete before routing',
        'Policy flag: request for quote needed after the request for information.',
      ),
      approvedBy(18 * DAY, MARCUS, 'Budget owner'),
      approvedBy(17 * DAY, SARAH, 'Category management'),
      ag(
        16 * DAY,
        'Drafted RFI-2026-007 for dry-type transformer manufacturers',
        'Questions on lead time by kVA rating, energy efficiency rule compliance and UL listing.',
      ),
      ag(
        2 * DAY,
        `Reminded ${marcus} to review and release the request for information`,
        'Sent in Teams.',
      ),
    ],
  });
}

function req1042(): ProcurementRequest {
  const polaris = supplierShort('sup-polaris');
  return seedRequest({
    id: 'REQ-1042',
    title: 'Second supplier for NEMA 4X enclosures',
    description: `Qualify a second fabricator for the stainless weatherproof (NEMA 4X) enclosures used in motor drive retrofit kits at the control panel shop. ${polaris} is the only supplier today.`,
    requesterId: 'sarah-chen',
    department: 'Category management, Electrical',
    costCenter: 'CC-5100',
    categoryId: 'cat-enclosures',
    categoryLabel: 'Enclosures & panel components',
    type: 'direct',
    amountUsd: 1_400_000,
    amountNote: 'Estimated annual spend moved to the second supplier',
    recurring: true,
    neededInDays: 120,
    createdH: 16 * DAY,
    stage: 'review',
    stageNote: 'Waiting on Quality',
    buyPath: 'Request for quote and quality qualification',
    suppliers: [
      knownSupplier(
        'sup-polaris',
        'Incumbent and only supplier of NEMA 4X enclosures',
        'SA-POLR-2022-008',
      ),
    ],
    checks: checks([
      [
        'pass',
        'No similar open request. No earlier attempt to find a second supplier on file.',
      ],
      [
        'flag',
        'Direct material change: a second supplier needs quality qualification and a supply agreement. Request for quote required.',
      ],
      [
        'pass',
        `Within the enclosures category budget. Spend moves from ${polaris}, so there is no net increase.`,
      ],
      ['na', 'Not required. No system access or personal data in scope.'],
      ['na', 'Not required until a supplier is selected.'],
      [
        'flag',
        `${polaris} is the only supplier of NEMA 4X enclosures (RSK-300). Risk 46, Medium.`,
        [{ kind: 'risk-alert', alertId: 'RSK-300' }],
      ],
    ]),
    approvals: approvals([
      { role: 'Budget owner', approver: MARCUS, doneH: 15 * DAY },
      { role: 'Quality', approver: PRIYA },
    ]),
    linked: [
      {
        kind: 'risk-alert',
        alertId: 'RSK-300',
        title: 'RSK-300',
        detail: `${polaris} is the only supplier of NEMA 4X enclosures`,
      },
      {
        kind: 'contract',
        contractId: 'SA-POLR-2022-008',
        detail: 'Incumbent supply agreement, renews every year',
      },
    ],
    recommendation: {
      title: 'Qualify a second fabricator for NEMA 4X enclosures',
      body: `${polaris} is the only supplier (RSK-300). The agent drafted a request for quote and a 12-week qualification plan for a second fabricator, covering UL Type 4X listing, sample enclosures for the control panel shop and a pilot order.`,
    },
    onApproved: 'sourcing',
    onApprovedNote:
      'All approvals complete. Request for quote opened for a second NEMA 4X enclosure supplier.',
    activitySeeds: [
      received(16 * DAY, 'sarah-chen', 'through web intake'),
      ag(
        16 * DAY - 1,
        'Classified as Enclosures & panel components, Direct',
        'Linked to RSK-300 and SA-POLR-2022-008.',
      ),
      ag(
        16 * DAY - 2,
        'Checks complete before routing',
        'Policy flag: request for quote and qualification needed.',
      ),
      approvedBy(15 * DAY, MARCUS, 'Budget owner'),
      routedTo(15 * DAY, PRIYA, 'Quality'),
      ag(2 * DAY, `Reminded ${personName('priya-raman')}`, 'Sent in Teams.'),
    ],
  });
}

function req1044(): ProcurementRequest {
  const kestrel = supplierShort('sup-kestrel');
  const lumen = supplierShort('sup-lumen');
  const sarah = personName('sarah-chen');
  const sam = personName('sam-whitfield');
  return seedRequest({
    id: 'REQ-1044',
    title: 'Second supplier for 50 HP motor drives',
    description: `Add a second supplier for 50 HP, 480 V motor drives. ${lumen} supplies 100% today from a single plant in China, with tariff exposure. Target a 30% share for the second supplier.`,
    requesterId: 'sarah-chen',
    department: 'Category management, Electrical',
    costCenter: 'CC-5100',
    categoryId: 'cat-drives',
    categoryLabel: 'Drives & automation',
    type: 'direct',
    amountUsd: 6_200_000,
    amountNote: '30% share of annual motor drive volume',
    recurring: true,
    neededInDays: 150,
    createdH: 14 * DAY,
    stage: 'sourcing',
    stageNote: `Request for quote in evaluation, ${kestrel} onboarding at 86%`,
    buyPath: 'Request for quote plus supplier onboarding',
    suppliers: [
      knownSupplier(
        'sup-kestrel',
        'Quoted $6,050 each, delivered to Hayward DC with duties paid. In onboarding',
        'NDA-KEST-2026-001',
      ),
      knownSupplier(
        'sup-lumen',
        'Incumbent, 100% of volume at $5,500 each, delivered with duties paid',
        'SA-LUMN-2025-002',
      ),
    ],
    checks: checks([
      ['pass', 'No similar open request for 50 HP drives.'],
      [
        'pass',
        'Direct second supplier: RFQ-2026-022 with three bidders satisfies the competitive bid rule.',
      ],
      [
        'pass',
        `Premium of $550 each over ${lumen} on about 1,020 drives is roughly $561K a year, funded from the drives and automation budget.`,
      ],
      ['na', 'Not required. No system access in scope.'],
      [
        'pass',
        'Mutual confidentiality agreement signed. The supply agreement is drafted after award.',
        [{ kind: 'contract', contractId: 'NDA-KEST-2026-001' }],
      ],
      [
        'pass',
        `${kestrel} preliminary risk 24, Low. A second supplier reduces the risk of relying on one supplier on RSK-305.`,
        [{ kind: 'risk-alert', alertId: 'RSK-305' }],
      ],
    ]),
    approvals: approvals([
      { role: 'Budget owner', approver: MARCUS, doneH: 13 * DAY },
      { role: 'Finance', approver: SAM, doneH: 12 * DAY },
      { role: 'Quality', approver: PRIYA },
    ]),
    linked: [
      {
        kind: 'onboarding',
        caseId: 'ONB-0087',
        title: `ONB-0087 ${getSupplier('sup-kestrel').name}`,
        detail: 'At approvals, 86% complete',
      },
      {
        kind: 'sourcing-event',
        eventId: 'RFQ-2026-022',
        title: 'RFQ-2026-022',
        detail: 'Motor drive second supplier, in evaluation',
      },
      {
        kind: 'material',
        materialId: 'MAT-VFD',
        detail: '3,400 drives a year, one supplier today',
      },
      {
        kind: 'risk-alert',
        alertId: 'RSK-305',
        title: 'RSK-305',
        detail: `${lumen} is the only supplier, with tariff exposure`,
      },
    ],
    recommendation: {
      title: `Award the second supplier slot to ${kestrel} once onboarding completes`,
      body: `${kestrel} is the only bidder meeting the UL listing and CE requirements within 10% of ${lumen} on delivered price. A 30% share removes the risk of relying on one supplier and the tariff surcharge risk (RSK-305). Quality sign-off waits on the last onboarding approvals.`,
    },
    onApproved: 'sourcing',
    onApprovedNote: `All approvals complete. ${kestrel} cleared to receive the award notice.`,
    activitySeeds: [
      received(14 * DAY, 'sarah-chen', 'through web intake'),
      ag(
        14 * DAY - 1,
        'Classified as Drives & automation, Direct',
        `One supplier: ${lumen} at 100% of MAT-VFD.`,
      ),
      ag(13 * DAY + 6, 'Opened RFQ-2026-022 with three bidders'),
      approvedBy(13 * DAY, MARCUS, 'Budget owner'),
      by(
        12 * DAY,
        SAM,
        `Finance approval: ${sam} approved`,
        'Above $5M a year.',
      ),
      ag(
        9 * DAY,
        `Started supplier onboarding for ${getSupplier('sup-kestrel').name}`,
        'ONB-0087 created from the request for quote shortlist.',
      ),
      ag(
        2 * DAY,
        `Bid comparison complete: ${kestrel} $6,050 each, delivered with duties paid`,
        'Within 10% of the incumbent price. Two other bids are higher.',
      ),
      ag(DAY, 'Onboarding at 86%: waiting on final approvals'),
    ],
  });
}

function req1049(): ProcurementRequest {
  const hanover = getSupplier('sup-hanover');
  return seedRequest({
    id: 'REQ-1049',
    title: 'Sample lot from Hanover for high-bay qualification',
    description: `Buy a qualification lot of 540 LED high-bay fixtures from ${hanover.name}, a bidder in RFP-2026-014, to run light-output, heat and certification tests before any award.`,
    requesterId: 'priya-raman',
    department: 'Supplier quality, Dallas DC',
    costCenter: 'CC-3310',
    categoryId: 'cat-lighting',
    categoryLabel: 'Lighting',
    type: 'direct',
    amountUsd: 48_000,
    amountNote: '540 fixtures at about $88.85 delivered',
    neededInDays: 28,
    createdH: 8 * DAY,
    stage: 'sourcing',
    stageNote: `${hanover.shortName} in risk review`,
    buyPath: 'Evaluation purchase under RFP-2026-014',
    suppliers: [
      knownSupplier(
        'sup-hanover',
        'RFP-2026-014 bidder. Prospect, not yet an approved supplier',
      ),
    ],
    checks: checks([
      ['pass', `No open request for ${hanover.shortName} samples.`],
      [
        'pass',
        'Qualification lot for a bidder in RFP-2026-014. Evaluation spend, not an award.',
      ],
      ['pass', 'CC-3310 has $410K remaining. The lot uses $48K.'],
      ['na', 'Not required. No system access or personal data in scope.'],
      [
        'na',
        'Not required. Sample terms are covered by the bid confidentiality agreement.',
      ],
      [
        'flag',
        `${hanover.shortName} is a prospect. Onboarding ONB-0091 is at risk review (52%). Preliminary risk 26, Low.`,
      ],
    ]),
    approvals: approvals([
      { role: 'Budget owner', approver: MARCUS, doneH: 20 },
      { role: 'Category management', approver: SARAH },
    ]),
    linked: [
      {
        kind: 'onboarding',
        caseId: 'ONB-0091',
        title: `ONB-0091 ${hanover.name}`,
        detail: 'At risk review, 52% complete',
      },
      {
        kind: 'sourcing-event',
        eventId: 'RFP-2026-014',
        title: 'RFP-2026-014',
        detail: 'Pillar Pro LED high-bays 2027-2029, bids in',
      },
      {
        kind: 'material',
        materialId: 'MAT-LED',
        detail: 'Pillar Pro LED high-bay light fixture, 150 W',
      },
    ],
    recommendation: {
      title: 'Approve the lot, hold the award until risk review closes',
      body: `Light-output and heat test data strengthens the bid comparison in RFP-2026-014. ${hanover.shortName} still needs to clear onboarding risk review before any award, so the lot is evaluation spend only.`,
    },
    onApproved: 'sourcing',
    onApprovedNote: `All approvals complete. Purchase order for the qualification lot sent to ${hanover.shortName}.`,
    activitySeeds: [
      received(8 * DAY, 'priya-raman', 'through web intake'),
      ag(
        8 * DAY - 1,
        'Classified as Lighting, Direct',
        'Linked to RFP-2026-014 and ONB-0091.',
      ),
      ag(
        8 * DAY - 2,
        'Checks complete before routing',
        `Flag: ${hanover.shortName} still in onboarding risk review.`,
      ),
      ag(3 * DAY, `Reminded ${personName('marcus-hale')}`, 'Sent in Teams.'),
      approvedBy(20, MARCUS, 'Budget owner'),
      routedTo(20, SARAH, 'Category management'),
    ],
  });
}

function req1050(): ProcurementRequest {
  const hollis = supplierShort('sup-hollis');
  const vitrex = supplierShort('sup-vitrex');
  return seedRequest({
    id: 'REQ-1050',
    title: `${hollis} 800 A breakers: stopgap order during ${vitrex} quarantine`,
    description: `Buy 80 circuit breakers, 800 A, from ${hollis}, the qualified backup supplier, to cover the Dallas control panel shop while ${vitrex} lot VX-24117 is quarantined.`,
    requesterId: 'marcus-hale',
    channel: 'teams',
    department: 'Supply chain, Dallas control panel shop',
    costCenter: 'CC-2250',
    categoryId: 'cat-circuit',
    categoryLabel: 'Circuit protection',
    type: 'direct',
    amountUsd: 321_600,
    amountNote: '80 breakers at $4,020',
    neededInDays: 35,
    createdH: 6 * DAY,
    stage: 'review',
    stageNote: 'Waiting on category management',
    buyPath: 'Release under SA-HOLL-2024-021',
    suppliers: [
      knownSupplier(
        'sup-hollis',
        'Qualified backup supplier for the 800 A breaker (MCCB-800A-H) at $4,020 each',
        'SA-HOLL-2024-021',
      ),
      knownSupplier(
        'sup-vitrex',
        'Main supplier at 80%. Lot VX-24117 quarantined, corrective action request SCAR-0192 open',
        'SA-VITX-2023-004',
      ),
    ],
    checks: checks([
      [
        'pass',
        'No open request for 800 A breakers. This is the first bridge order this year.',
      ],
      [
        'pass',
        'Priced under SA-HOLL-2024-021 as a qualified backup supplier. No competitive bids needed.',
        [{ kind: 'contract', contractId: 'SA-HOLL-2024-021' }],
      ],
      ['pass', 'Funded from CC-2250 contingency, which has $740K remaining.'],
      [
        'na',
        'Not required. Existing qualified supplier, no new system access.',
      ],
      ['na', 'Not required. Terms follow the existing supply agreement.'],
      [
        'pass',
        `${hollis} risk 18, Low. Moving volume away from ${vitrex} (risk 82, Critical, RSK-301) reduces exposure.`,
        [{ kind: 'risk-alert', alertId: 'RSK-301' }],
      ],
    ]),
    approvals: approvals([
      { role: 'Budget owner', approver: MARCUS, doneH: 6 * DAY },
      { role: 'Quality', approver: PRIYA, doneH: DAY },
      { role: 'Category management', approver: SARAH },
    ]),
    linked: [
      {
        kind: 'risk-alert',
        alertId: 'RSK-301',
        title: 'RSK-301',
        detail: `${vitrex} defective product, lot VX-24117`,
      },
      {
        kind: 'contract',
        contractId: 'SA-HOLL-2024-021',
        detail: `${hollis} supply agreement, 800 A breakers`,
      },
      {
        kind: 'material',
        materialId: 'MAT-MCCB',
        detail: `${vitrex} 80%, ${hollis} 20%`,
      },
    ],
    recommendation: {
      title: `Release 80 breakers from ${hollis} now`,
      body: `The Dallas control panel shop has 6 weeks of 800 A breakers on hand after the ${vitrex} quarantine. ${hollis} can ship 80 in 5 weeks under SA-HOLL-2024-021, which keeps panel builds running until the ${vitrex} corrective action request closes.`,
    },
    onApproved: 'ordering',
    onApprovedNote:
      'All approvals complete. Draft PO for 80 breakers sent to the buyer for release.',
    activitySeeds: [
      received(6 * DAY, 'marcus-hale', 'through Teams'),
      ag(
        6 * DAY - 1,
        'Classified as Circuit protection, Direct',
        'Linked to RSK-301 and SA-HOLL-2024-021.',
      ),
      ag(6 * DAY - 2, 'Checks complete before routing', 'All checks passed.'),
      approvedBy(6 * DAY - 3, MARCUS, 'Budget owner'),
      approvedBy(
        DAY,
        PRIYA,
        'Quality',
        `${hollis} UL listing and factory location confirmed on UL’s certified-factory list.`,
      ),
      routedTo(DAY, SARAH, 'Category management'),
    ],
  });
}

export function buildDirectRequests(): ProcurementRequest[] {
  return [req1039(), req1042(), req1044(), req1049(), req1050()];
}
